import React, { useState, useEffect, useRef } from "react";
import html2canvas from "html2canvas";
import "./PublicDecks.css";
import FullScreenModal from './FullScreenModal';

function PublicDecks({ cardTypeOptions, levelOptions, sexOptions, elementOptions, subElementOptions, powerOptions, reloadTrigger, setReloadTrigger })
{
  const [decks, setDecks] = useState([]);
  const [selectedDeck, setSelectedDeck] = useState(null);
  const [deckCards, setDeckCards] = useState([]);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // ⭐ STEP2 追加 state
  const [searchText, setSearchText] = useState("");
  const [filterContinent, setFilterContinent] = useState("");
  const [filterUser, setFilterUser] = useState("");
  const [sortKey, setSortKey] = useState("update_date");
  const [sortOrder, setSortOrder] = useState("desc");

  // サマリー用
  const [sortPattern, setSortPattern] = useState("element");

  // 任意の項目を集計する汎用関数
  const countByKey = (deck, key) => {
    return deck.reduce((acc, card) => {
      const value = card[key];
      if (value != null) {
        acc[value] = (acc[value] || 0) + 1;
      }
      return acc;
    }, {});
  };

  const getLabel = (options, codeKey, labelKey, value) => {
    const found = options.find(o => String(o[codeKey]) === String(value));
    return found ? found[labelKey] : "(不明)";
  };

  // ソート関数
  const sortMainDeck = (cards) => {
    const getSortKey = (card, key) => card[key] ?? '';

    const compare = (a, b, keys) => {
      for (const key of keys) {
        const valA = a[key];
        const valB = b[key];
        // null/undefined を後ろに回す
        if (valA == null && valB != null) return 1;
        if (valA != null && valB == null) return -1;
        if (valA == null && valB == null) continue;
        // 通常の比較（数値と文字列の両方に対応）
        const diff = valA.toString().localeCompare(valB.toString(), undefined, { numeric: true });
        if (diff !== 0) return diff;
      }
      return 0;
    };

    if (sortPattern === 'element') {
      return [...cards].sort((a, b) => compare(a, b, ['card_type_code', 'element_code', 'power_code']));
    } else if (sortPattern === 'power') {
      return [...cards].sort((a, b) => compare(a, b, ['card_type_code', 'power_code', 'element_code']));
    } else if (sortPattern === 'subelement') {
      return [...cards].sort((a, b) => compare(a, b, ['card_type_code', 'sub_element_code', 'element_code', 'power_code']));
    } else if (sortPattern === 'cardno') {
      return [...cards].sort((a, b) => compare(a, b, ['card_id']));
    }
    return cards;
  };

  // デッキコピー関数
  const copyDeck = async (deckId) => {
    const token = localStorage.getItem("token");
    if (!token) {
        alert("コピーにはログインが必要です");
        return;
    }

    try {
        const res = await fetch(`/api/save_decks/copy/${deckId}`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${token}`
        }
        });

        const data = await res.json();

        if (res.ok) {
            setReloadTrigger(t => t + 1);
            alert(`公開されているデッキを、自分のデッキへコピーしました！\n新しいデッキ名： ${data.deckName}`);
            // 🔥 必要ならコピー直後に DeckBuilder に遷移させることも可能
            // navigate(`/my-deck/${data.newDeckId}`);
        } else {
            alert(data.error || "コピー失敗");
        }
    } catch (e) {
        console.error("コピー API エラー:", e);
        alert("通信エラーが起きました");
    }
  };



  useEffect(() => {
    fetch("/api/public_decks")
      .then((res) => res.json())
      .then((data) => setDecks(data))
      .catch((err) => console.error("公開デッキ取得エラー:", err));
  }, [reloadTrigger]);

  const filteredDecks = decks
    .filter((deck) =>
      searchText
        ? deck.deck_name.toLowerCase().includes(searchText.toLowerCase()) ||
          deck.deck_tag?.toLowerCase().includes(searchText.toLowerCase()) ||
          deck.user_name.toLowerCase().includes(searchText.toLowerCase())
        : true
    )
    .filter((deck) =>
      filterContinent ? deck.continent_name === filterContinent : true
    )
    .filter((deck) => (filterUser ? deck.user_name === filterUser : true))
    .sort((a, b) => {
      let A = a[sortKey];
      let B = b[sortKey];

      if (sortKey.includes("date")) {
        A = new Date(A);
        B = new Date(B);
      } else {
        A = A.toString().toLowerCase();
        B = B.toString().toLowerCase();
      }
      if (A < B) return sortOrder === "asc" ? -1 : 1;
      if (A > B) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

  const fetchDeckDetails = async (deckId) => {
    const res = await fetch(`/api/save_decks/${deckId}`);
    const data = await res.json();
    setSelectedDeck(data.deck);
    setDeckCards(data.cards);
    setIsDetailModalOpen(true);
  };

  const modalRef = useRef(null);

  //画像保存
  const saveAsImage = async () => {
    if (!modalRef.current) return;

    const canvas = await html2canvas(modalRef.current, {
      scale: 3,          // 解像度を高める
      useCORS: true,     // 画像が外部の場合でも読み込む
      scrollY: -window.scrollY, // ページスクロールを補正
      windowWidth: document.body.scrollWidth, // 横幅もフルでキャプチャ
    });
    const dataURL = canvas.toDataURL("image/png");

    const link = document.createElement("a");
    link.href = dataURL;
    link.download = `${selectedDeck.deck_name}.png`;
    link.click();
  };

  // 支援リンク用
  const [showSupportMenu, setShowSupportMenu] = useState(false);

  return (
    <div className="public-decks-container">
      <div className="page-title-row">
        <h2 className="page-title">公開デッキ一覧</h2>

        <div
          className="support-wrapper"
          onMouseLeave={() => setShowSupportMenu(false)}
        >
          <button
            className="support-trigger"
            data-tooltip={"※ 本支援はデジタルコンテンツ等の対価ではなく、\n　 サーバー運営への任意の寄付です。"}
            onClick={() => setShowSupportMenu(v => !v)}
          >
            このサイトを支援する
          </button>

          {showSupportMenu && (
            <div className="support-menu">
              <a
                href="https://qr.paypay.ne.jp/p2p01_vVUzpqT3lxnj4f3X"
                target="_blank"
                rel="noopener noreferrer"
                data-tooltip={"PayPayで支援できます\n・匿名OK\n・金額自由"}
              >
                PayPayで支援
              </a>
              <a
                href="https://ofuse.me/3ff0e549/letter"
                target="_blank"
                rel="noopener noreferrer"
                data-tooltip={"OFUSE経由で支援できます\n・アカウント作成不要\n・匿名OK\n・クレジットカード対応\n・金額自由"}
              >
                OFUSEで支援
              </a>
            </div>
          )}
        </div>
      </div>

      {/* ====================== 🔍 STEP2: フィルタ UI ====================== */}
      <div className="filter-box">
        <input
          type="text"
          placeholder="🔍 デッキ名 / 作者名 / タグ検索"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />

        <select
          value={filterContinent}
          onChange={(e) => setFilterContinent(e.target.value)}
        >
          <option value="">大陸フィルタ</option>
          {[...new Set(decks.map((d) => d.continent_name))].map((name) => (
            <option key={name}>{name}</option>
          ))}
        </select>

        <select
          value={filterUser}
          onChange={(e) => setFilterUser(e.target.value)}
        >
          <option value="">製作者フィルタ</option>
          {[...new Set(decks.map((d) => d.user_name))].map((name) => (
            <option key={name}>{name}</option>
          ))}
        </select>

        <select className="update_date" onChange={(e) => setSortKey(e.target.value)}>
          <option value="update_date">更新日</option>
          <option value="deck_name">デッキ名</option>
        </select>

        <button
          onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
        >
          {sortOrder === "asc" ? "▲ 昇順" : "▼ 降順"}
        </button>
      </div>

      {/* ====================== 🔳 デッキ一覧 ====================== */}
      <div className="deck-grid">
        {filteredDecks.map((deck) => (
          <div
            key={deck.deck_id}
            className="deck-card"
            onClick={() => fetchDeckDetails(deck.deck_id)}
          >
            <img
              src={`${process.env.PUBLIC_URL}/assets/${deck.main_card_image}`}
              alt={deck.deck_name}
              className="main-card-image"
            />
            <div className="deck-info">
              <h4>{deck.deck_name}</h4>
              <p className="deck-owner">👤 {deck.user_name}</p>
              <p className="deck-tag">🏷 {deck.deck_tag || "(タグなし)"}</p>
              <p className="deck-date">
                更新日：{new Date(deck.update_date).toLocaleDateString()}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* ====================== 🪟 モーダル ====================== */}
      {isDetailModalOpen && selectedDeck && (
          <FullScreenModal
            isOpen={isDetailModalOpen}
            onClose={() => setIsDetailModalOpen(false)}
          >
            <div ref={modalRef} className="capture-target">
              <div className="modal-header-row">
                  <h2 className="modal-title">{selectedDeck.deck_name}</h2>
                  <button
                      className="copy-button"
                      onClick={() => copyDeck(selectedDeck.deck_id)}
                  >
                      📋 自分のデッキにコピー
                  </button>
                  <button className="image-button" onClick={saveAsImage}>画像として保存</button>
              </div>
              <p><strong>作成者：</strong> {selectedDeck.user_name}</p>
              <p><strong>タグ：</strong> {selectedDeck.deck_tag || "(なし)"}</p>
              <p><strong>説明：</strong> {selectedDeck.description || "(なし)"}</p>

              {/* ▼▼ サマリー ▼▼ */}
              <div className="deck-summary-grid">
                  {[
                  { title: 'カード分類', key: 'card_type_code', options: cardTypeOptions, codeKey: 'card_type_code', labelKey: 'card_type_name' },
                  { title: '召喚師レベル', key: 'level_code', options: levelOptions, codeKey: 'level_code', labelKey: 'level' },
                  { title: '性別', key: 'sex_code', options: sexOptions, codeKey: 'sex_code', labelKey: 'sex' },
                  { title: '属性', key: 'element_code', options: elementOptions, codeKey: 'element_code', labelKey: 'element_name', onlyTypeCode2: true },
                  { title: 'サブ属性', key: 'sub_element_code', options: subElementOptions, codeKey: 'element_code', labelKey: 'element_name' },
                  { title: 'パワー', key: 'power_code', options: powerOptions, codeKey: 'power_code', labelKey: 'power' },
                  ].map(({ title, key, options, codeKey, labelKey, onlyTypeCode2 }) => {
                  const targetDeck = onlyTypeCode2
                      ? deckCards.filter(card => card.card_type_code === 2)
                      : deckCards;

                  const counts = countByKey(targetDeck, key);
                  if (Object.keys(counts).length === 0) return null;

                  return (
                      <div className="summary-category" key={key}>
                      <h4>{title}</h4>
                      <div className="summary-items">
                          {Object.entries(counts)
                          .sort(([a], [b]) => a - b)
                          .map(([code, count]) => (
                              <div key={code} className="summary-item">
                              <span className="item-label">
                                  {getLabel(options, codeKey, labelKey, code)}
                              </span>
                              <span className="item-count">{count}</span>
                              </div>
                          ))}
                      </div>
                      </div>
                  );
                  })}
              </div>

              {/* ▼▼ ソート選択 ▼▼ */}
              <div className="sort-selector">
                  <label>ゾーン並び順:</label>
                  <select value={sortPattern} onChange={(e) => setSortPattern(e.target.value)}>
                  <option value="element">属性</option>
                  <option value="power">パワー</option>
                  <option value="subelement">サブ属性</option>
                  <option value="cardno">カードNo.</option>
                  </select>
              </div>

              {/* ▼▼ 召喚師 ▼▼ */}
              {deckCards.some(card => card.card_type_code === 1) && (
                  <>
                  <ul className="deck-list">
                      {deckCards
                      .filter(card => card.card_type_code === 1)
                      .sort((a, b) =>
                          a.level_code !== b.level_code
                          ? a.level_code - b.level_code
                          : a.card_id.localeCompare(b.card_id)
                      )
                      .map((card, index) => (
                          <li key={`type1-${index}`}>
                          <img
                              className="public-list-card-image"
                              src={`${process.env.PUBLIC_URL}/assets/${card.card_image}`}
                              title={card.card_name}
                          />
                          </li>
                      ))}
                  </ul>
                  </>
              )}

              {/* ▼▼ メインデッキ ▼▼ */}
              <ul className="deck-list">
                  {sortMainDeck(deckCards.filter(card => card.card_type_code !== 1)).map((card, index) => (
                  <li key={index}>
                      <img
                      className="public-list-card-image"
                      src={`${process.env.PUBLIC_URL}/assets/${card.card_image}`}
                      title={card.card_name}
                      />
                  </li>
                  ))}
              </ul>
            </div>
          </FullScreenModal>
      )}
    </div>
  );
}

export default PublicDecks;
